// Moldable play sand on a voxel grid. A knife clears one column at a time; a
// thin end slice tips over its outer edge as a rigid body and lands on its
// back, cracks, and sheds crumbs. Loose grains follow falling-sand rules, as in
// Noita (Purho, GDC 2019): fall if the cell below is empty, else slide down
// diagonally. A cohesion rule is added: a grain moves only when few neighbours
// hold it, so cut walls stand and exposed edges crumble.
// Every grain keeps its rest coordinate for the solid-texture colours.
const NEIGHBOURS=[];for(let z=-1;z<=1;z++)for(let y=-1;y<=1;y++)for(let x=-1;x<=1;x++)if(x||y||z)NEIGHBOURS.push([x,y,z]);
const SLIDES=[[1,0],[-1,0],[0,1],[0,-1],[1,1],[1,-1],[-1,1],[-1,-1]];

export class SandLoaf{
  constructor({min,max,cell,random=Math.random,gravity=9}){
    this.loaf={min:[...min],max:[...max]};this.cell=cell;this.random=random;
    const height=max[1]-min[1];this.height=height;this.gravity=gravity*height;
    const padX=height*1.2+cell*2,padZ=cell*5;
    this.origin=[min[0]-padX,min[1],min[2]-padZ];
    this.nx=Math.ceil((max[0]-min[0]+2*padX)/cell);this.ny=Math.ceil(height/cell)+3;this.nz=Math.ceil((max[2]-min[2]+2*padZ)/cell);
    const n=this.nx*this.ny*this.nz;
    this.occupancy=new Uint8Array(n);this.rest=new Float32Array(n*3);this.standing=new Uint8Array(n);this.loose=new Uint8Array(n);
    this.queued=new Uint8Array(n);this.still=new Uint8Array(n);this.active=[];
    this.slabs=[];this.parting=[];this.version=0;this.cuts=0;this.landings=0;this.carved=0;this.clock=0;
    this.onLand=null;this.onCrumbs=null;
    this.fill();
  }
  index(i,j,k){return i+this.nx*(j+this.ny*k);}
  inside(i,j,k){return i>=0&&j>=0&&k>=0&&i<this.nx&&j<this.ny&&k<this.nz;}
  center(i,j,k,out=[0,0,0]){const h=this.cell,o=this.origin;out[0]=o[0]+(i+.5)*h;out[1]=o[1]+(j+.5)*h;out[2]=o[2]+(k+.5)*h;return out;}
  column(x){return Math.floor((x-this.origin[0])/this.cell);}
  fill(){
    this.occupancy.fill(0);this.standing.fill(0);this.loose.fill(0);this.queued.fill(0);this.still.fill(0);this.active.length=0;this.slabs.length=0;this.parting.length=0;
    const {min,max}=this.loaf,c=[0,0,0];let lo=Infinity,hi=-Infinity;
    for(let k=0;k<this.nz;k++)for(let j=0;j<this.ny;j++)for(let i=0;i<this.nx;i++){
      this.center(i,j,k,c);
      if(c[0]<min[0]||c[0]>max[0]||c[1]<min[1]||c[1]>max[1]||c[2]<min[2]||c[2]>max[2])continue;
      const q=this.index(i,j,k);this.occupancy[q]=1;this.standing[q]=1;this.rest.set(c,q*3);lo=Math.min(lo,i);hi=Math.max(hi,i);
    }
    this.blocks=[[lo,hi]];this.cuts=0;this.version++;
  }
  // The highest standing grain in column i, as a height; -Infinity if none.
  top(i){
    if(i<0||i>=this.nx)return -Infinity;
    for(let j=this.ny-1;j>=0;j--)for(let k=0;k<this.nz;k++)if(this.occupancy[this.index(i,j,k)])return this.origin[1]+(j+1)*this.cell;
    return -Infinity;
  }
  blockAt(i){return this.blocks.find(([a,b])=>i>=a&&i<=b)??null;}
  // The knife clears column i above the blade's edge.
  carve(i,edge){
    const block=this.blockAt(i);if(!block)return 0;let removed=0;
    for(let j=0;j<this.ny;j++){
      if(this.origin[1]+(j+.5)*this.cell<=edge)continue;
      for(let k=0;k<this.nz;k++){const q=this.index(i,j,k);if(this.occupancy[q]&&this.standing[q]){this.clear(q);removed++;}}
    }
    if(removed){this.version++;this.carved+=removed;}
    return removed;
  }
  clear(q){this.occupancy[q]=0;this.standing[q]=0;this.loose[q]=0;}
  // The blade reached the floor: the block splits, and a thin end piece tips.
  finishCut(i){
    const block=this.blockAt(i);if(!block)return null;
    for(let j=0;j<this.ny;j++)for(let k=0;k<this.nz;k++){const q=this.index(i,j,k);if(this.standing[q])this.clear(q);}
    this.blocks=this.blocks.filter(b=>b!==block);
    const left=[block[0],i-1],right=[i+1,block[1]];
    for(const piece of [left,right])if(piece[1]>=piece[0])this.blocks.push(piece);
    this.blocks.sort((a,b)=>a[0]-b[0]);this.cuts++;this.version++;
    const thin=piece=>piece[1]>=piece[0]&&(piece[1]-piece[0]+1)*this.cell<this.height*.62;
    const end=(piece,dir)=>!this.blocks.some(b=>b!==piece&&(dir>0?b[0]>piece[1]:b[1]<piece[0]));
    for(const [piece,dir] of [[right,1],[left,-1]]){
      const found=this.blocks.find(b=>b[0]===piece[0]&&b[1]===piece[1]);
      if(found&&thin(found)&&end(found,dir))return this.tip(found,dir);
    }
    // Both halves stand: the end one slides two cells away so the cut shows.
    for(const [piece,dir] of [[right,1],[left,-1]]){
      const found=this.blocks.find(b=>b[0]===piece[0]&&b[1]===piece[1]);
      if(found&&end(found,dir)){this.parting.push({block:found,dir,left:2});break;}
    }
    return null;
  }
  // Slide a standing block one cell along x, if the way is clear.
  shift(block,dir){
    const [a,b]=block,cells=[];
    for(let k=0;k<this.nz;k++)for(let j=0;j<this.ny;j++)for(let i=a;i<=b;i++){const q=this.index(i,j,k);if(this.standing[q])cells.push(q);}
    if(cells.some(q=>{const i=q%this.nx+dir;return i<0||i>=this.nx||(this.occupancy[q+dir]&&!this.standing[q+dir]);}))return false;
    cells.sort((p,q)=>dir>0?(q%this.nx)-(p%this.nx):(p%this.nx)-(q%this.nx));
    for(const q of cells){this.move(q,q+dir);this.standing[q+dir]=1;}
    block[0]+=dir;block[1]+=dir;this.version++;return true;
  }
  tip(piece,dir){
    const cells=[],c=[0,0,0];
    for(let k=0;k<this.nz;k++)for(let j=0;j<this.ny;j++)for(let i=piece[0];i<=piece[1];i++){
      const q=this.index(i,j,k);if(!this.standing[q])continue;
      cells.push({i,j,k,rest:this.rest.slice(q*3,q*3+3)});this.clear(q);
    }
    this.blocks=this.blocks.filter(b=>b!==piece);this.version++;
    const h=this.cell,thickness=(piece[1]-piece[0]+1)*h,H=this.height,d=Math.hypot(thickness,H);
    const pivot=[this.origin[0]+(dir>0?piece[1]+1:piece[0])*h,this.origin[1]];
    const lean=Math.atan2(thickness/2,H/2),k=3*this.gravity/(2*d);
    // The blade's push carries the slice past its balance point.
    const omega=Math.sqrt(2*k*(1-Math.cos(lean)))*1.25+.35;
    const slab={cells,dir,piece:[...piece],pivot,angle:0,omega,k,lean,landed:false};
    this.slabs.push(slab);return slab;
  }
  // Rotation of a slab point about its pivot, in the x-y plane.
  turn(slab,x,y,out){
    const phi=-slab.dir*slab.angle,c=Math.cos(phi),s=Math.sin(phi),dx=x-slab.pivot[0],dy=y-slab.pivot[1];
    out[0]=slab.pivot[0]+dx*c-dy*s;out[1]=slab.pivot[1]+dx*s+dy*c;return out;
  }
  step(dt){
    if(!(dt>0))return;
    for(const slab of this.slabs){
      slab.omega+=slab.k*Math.sin(slab.angle-slab.lean)*dt;
      const next=Math.min(Math.PI/2,slab.angle+slab.omega*dt);
      // A slice that meets other sand on the way down rests against it there.
      if(this.touches(slab,next)){this.land(slab);continue;}
      slab.angle=next;if(next>=Math.PI/2)this.land(slab);
    }
    this.slabs=this.slabs.filter(slab=>!slab.landed);
    // Grains move on a fixed 30 Hz clock, one cell per tick.
    this.clock+=dt;
    for(let ticks=0;this.clock>=1/30&&ticks<3;ticks++){this.clock-=1/30;this.tick();}
    if(this.clock>1/30)this.clock=0;
  }
  // Does any grain of the slice, turned to this angle, fall in an occupied cell?
  touches(slab,angle){
    const saved=slab.angle;slab.angle=angle;const c=[0,0,0],p=[0,0,0],h=this.cell;let hit=false;
    for(const cell of slab.cells){
      this.center(cell.i,cell.j,cell.k,c);this.turn(slab,c[0],c[1],p);
      const i=Math.floor((p[0]-this.origin[0])/h),j=Math.floor((p[1]-this.origin[1])/h);
      if(this.inside(i,j,cell.k)&&this.occupancy[this.index(i,j,cell.k)]){hit=true;break;}
    }
    slab.angle=saved;return hit;
  }
  // The slice settles at its current angle. A crack runs across it in its own
  // frame; the outer part moves one cell further out. Every grid cell under
  // the turned slice is then filled by looking back into the slice, so a
  // leaning slice has no holes and nothing jumps.
  land(slab){
    slab.landed=true;this.landings++;
    const h=this.cell,key=(i,j,k)=>(k*1024+j)*4096+i,local=new Map();
    for(const cell of slab.cells)local.set(key(cell.i,cell.j,cell.k),cell.rest);
    let jLo=Infinity,jHi=-Infinity;for(const cell of slab.cells){jLo=Math.min(jLo,cell.j);jHi=Math.max(jHi,cell.j);}
    const crumbs=[],c=[0,0,0],p=[0,0,0];
    if(jHi-jLo>=4){
      // Crack toward one end, so the face on the slice mostly survives.
      const u=this.random(),crack=Math.round(jLo+(jHi-jLo)*(u<.5?.2+.24*u:.56+.24*u));
      for(const cell of slab.cells){
        if(cell.j<crack)continue;
        local.delete(key(cell.i,cell.j,cell.k));
        if(cell.j===crack){this.center(cell.i,cell.j,cell.k,c);this.turn(slab,c[0],c[1],p);crumbs.push([p[0],p[1],c[2]]);}
      }
      for(const cell of slab.cells)if(cell.j>crack)local.set(key(cell.i,cell.j+1,cell.k),cell.rest);
    }
    // Bounds of the turned slice on the grid.
    let iLo=Infinity,iHi=-Infinity,yLo=Infinity,yHi=-Infinity,kLo=Infinity,kHi=-Infinity;
    for(const value of local.keys()){
      const i=value%4096,j=Math.floor(value/4096)%1024,k=Math.floor(value/(4096*1024));
      this.center(i,j,k,c);this.turn(slab,c[0],c[1],p);
      iLo=Math.min(iLo,p[0]);iHi=Math.max(iHi,p[0]);yLo=Math.min(yLo,p[1]);yHi=Math.max(yHi,p[1]);kLo=Math.min(kLo,k);kHi=Math.max(kHi,k);
    }
    const at=new Set(),phi=-slab.dir*slab.angle,cos=Math.cos(phi),sin=Math.sin(phi),[px,py]=slab.pivot;
    const i0=Math.max(0,Math.floor((iLo-this.origin[0])/h)-1),i1=Math.min(this.nx-1,Math.floor((iHi-this.origin[0])/h)+1);
    const j0=Math.max(0,Math.floor((yLo-this.origin[1])/h)-1),j1=Math.min(this.ny-1,Math.floor((yHi-this.origin[1])/h)+1);
    for(let k=kLo;k<=kHi;k++)for(let j=j0;j<=j1;j++)for(let i=i0;i<=i1;i++){
      const q=this.index(i,j,k);if(this.occupancy[q])continue;
      this.center(i,j,k,c);
      // Inverse turn: from the grid back into the standing slice.
      const dx=c[0]-px,dy=c[1]-py,lx=px+dx*cos+dy*sin,ly=py-dx*sin+dy*cos;
      const li=Math.floor((lx-this.origin[0])/h),lj=Math.floor((ly-this.origin[1])/h);
      const rest=local.get(key(li,lj,k));if(!rest)continue;
      this.occupancy[q]=1;this.standing[q]=0;this.loose[q]=0;this.rest.set(rest,q*3);at.add(q);
    }
    // Exposed edge grains loosen and slide off in small piles.
    for(const q of at){if(this.neighbours(q)<13&&this.random()<.45){this.loose[q]=1;this.plain(q);this.wake(q);}}
    this.version++;this.onLand?.(slab,crumbs);
  }
  // A crumb that leaves the face core shows its layer colour, not stray face ink.
  plain(q){
    const {min,max}=this.loaf,mid=(min[2]+max[2])/2,R=Math.min(max[1]-min[1],max[2]-min[2])*.37*1.12;
    const y=this.rest[q*3+1]-(min[1]+max[1])/2,z=this.rest[q*3+2]-mid,r=Math.hypot(y,z);
    if(r<R){const side=z<0?-1:1;this.rest[q*3+2]=mid+side*Math.sqrt(Math.max(0,R*R-y*y));}
  }
  neighbours(q){
    const i=q%this.nx,j=Math.floor(q/this.nx)%this.ny,k=Math.floor(q/(this.nx*this.ny));let count=0;
    for(const [x,y,z] of NEIGHBOURS){const a=i+x,b=j+y,c=k+z;if(b<0){count++;continue;}if(this.inside(a,b,c)&&this.occupancy[this.index(a,b,c)])count++;}
    return count;
  }
  wake(q){if(!this.queued[q]){this.queued[q]=1;this.still[q]=0;this.active.push(q);}}
  move(from,to){
    this.occupancy[to]=1;this.rest[to*3]=this.rest[from*3];this.rest[to*3+1]=this.rest[from*3+1];this.rest[to*3+2]=this.rest[from*3+2];
    this.loose[to]=this.loose[from];this.standing[to]=0;this.clear(from);
    if(this.queued[from]){this.queued[from]=0;}
  }
  tick(){
    for(const part of this.parting){if(part.left>0&&this.blocks.includes(part.block)&&this.shift(part.block,part.dir))part.left--;else part.left=0;}
    this.parting=this.parting.filter(part=>part.left>0);
    if(!this.active.length)return;
    const nx=this.nx,ny=this.ny,list=this.active.filter(q=>this.queued[q]).sort((a,b)=>(Math.floor(a/nx)%ny)-(Math.floor(b/nx)%ny));
    this.active=[];let moved=false;
    for(const q of list){
      this.queued[q]=0;
      if(!this.occupancy[q]||this.standing[q])continue;
      const i=q%nx,j=Math.floor(q/nx)%ny,k=Math.floor(q/(nx*ny)),n=this.neighbours(q);
      const hold=this.loose[q]?26:11;let target=-1;
      if(j>0&&n<hold){
        const below=this.index(i,j-1,k);
        if(!this.occupancy[below])target=below;
        else if(n<(this.loose[q]?26:8)){
          const start=Math.floor(this.random()*8);
          for(let s=0;s<8&&target<0;s++){
            const [dx,dz]=SLIDES[(start+s)%8],a=i+dx,c=k+dz;
            if(!this.inside(a,j-1,c))continue;
            const side=this.index(a,j,c),down=this.index(a,j-1,c);
            if(!this.occupancy[side]&&!this.occupancy[down])target=down;
          }
        }
      }
      if(target>=0){
        this.move(q,target);moved=true;this.wake(target);
        // Grains above may follow into the space.
        for(let x=-1;x<=1;x++)for(let z=-1;z<=1;z++){const a=i+x,c=k+z;if(this.inside(a,j+1,c)){const up=this.index(a,j+1,c);if(this.occupancy[up]&&!this.standing[up])this.wake(up);}}
      }else if(++this.still[q]<3){this.queued[q]=1;this.active.push(q);}
      else this.loose[q]=0;
    }
    if(moved)this.version++;
  }
  // A fingertip pushes a dent into the sand around point.
  // With a direction, nothing deeper than `limit` along it is pushed away.
  press(point,radius,direction=null,limit=Infinity){
    const h=this.cell,r2=radius*radius,c=[0,0,0];let removed=0;
    const lo=[0,1,2].map(a=>Math.max(0,Math.floor((point[a]-radius-this.origin[a])/h))),hi=[0,1,2].map((a,axis)=>Math.min([this.nx,this.ny,this.nz][axis]-1,Math.floor((point[a]+radius-this.origin[a])/h)));
    for(let k=lo[2];k<=hi[2];k++)for(let j=lo[1];j<=hi[1];j++)for(let i=lo[0];i<=hi[0];i++){
      const q=this.index(i,j,k);if(!this.occupancy[q])continue;this.center(i,j,k,c);
      if((c[0]-point[0])**2+(c[1]-point[1])**2+(c[2]-point[2])**2>r2)continue;
      if(direction&&c[0]*direction[0]+c[1]*direction[1]+c[2]*direction[2]>limit)continue;
      this.clear(q);removed++;
    }
    if(!removed)return 0;
    // Grains around the dent may lose their footing.
    const R=radius+2*h;
    for(let k=Math.max(0,lo[2]-2);k<=Math.min(this.nz-1,hi[2]+2);k++)for(let j=Math.max(0,lo[1]-2);j<=Math.min(this.ny-1,hi[1]+2);j++)for(let i=Math.max(0,lo[0]-2);i<=Math.min(this.nx-1,hi[0]+2);i++){
      const q=this.index(i,j,k);if(!this.occupancy[q]||this.standing[q])continue;this.center(i,j,k,c);
      if((c[0]-point[0])**2+(c[1]-point[1])**2+(c[2]-point[2])**2<=R*R)this.wake(q);
    }
    this.version++;this.carved+=removed;return removed;
  }
  get moving(){return this.slabs.length>0||this.active.length>0||this.parting.length>0;}
}
